import React from 'react';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { useChartColors } from '../../hooks/useDarkMode';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

function WeightChart({ metrics }) {
  const colors = useChartColors();

  // Filter metrics with weight data and sort by date
  const weightData = metrics
    .filter(m => m.weight_kg !== null)
    .sort((a, b) => new Date(a.date) - new Date(b.date));

  // Calculate 7-day moving average
  const calculateMovingAverage = (data, window = 7) => {
    const result = [];
    for (let i = 0; i < data.length; i++) {
      const start = Math.max(0, i - window + 1);
      const subset = data.slice(start, i + 1);
      const avg = subset.reduce((sum, d) => sum + parseFloat(d.weight_kg), 0) / subset.length;
      result.push(avg);
    }
    return result;
  };

  const labels = weightData.map(m => {
    const date = new Date(m.date);
    const label = `${date.getDate()}.${date.getMonth() + 1}`;
    return m.exclude_from_trends ? `${label}*` : label;
  });

  const weights = weightData.map(m => parseFloat(m.weight_kg));
  const movingAvg = calculateMovingAverage(weightData);

  const excludedCount = weightData.filter(m => m.exclude_from_trends).length;

  const pointColors = weightData.map(m =>
    m.exclude_from_trends
      ? (colors.isDark ? 'rgba(156, 163, 175, 0.3)' : 'rgba(107, 114, 128, 0.3)')
      : colors.teal.border
  );
  const pointBorderColors = weightData.map(m =>
    m.exclude_from_trends
      ? (colors.isDark ? 'rgba(156, 163, 175, 0.6)' : 'rgba(107, 114, 128, 0.5)')
      : colors.teal.border
  );

  const data = {
    labels,
    datasets: [
      {
        label: 'Gewicht',
        data: weights,
        borderColor: colors.teal.border,
        backgroundColor: colors.teal.background,
        pointBackgroundColor: pointColors,
        pointBorderColor: pointBorderColors,
        pointRadius: 3,
        pointHoverRadius: 5,
        tension: 0.1,
      },
      {
        label: '7-Tage-Durchschnitt',
        data: movingAvg,
        borderColor: colors.pink.border,
        backgroundColor: colors.pink.background,
        borderWidth: 2,
        pointRadius: 0,
        borderDash: [5, 5],
        tension: 0.3,
      }
    ]
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          color: colors.textColor,
          usePointStyle: true,
          padding: 20,
        }
      },
      tooltip: {
        mode: 'index',
        intersect: false,
        backgroundColor: colors.tooltip.background,
        titleColor: colors.tooltip.text,
        bodyColor: colors.tooltip.text,
        borderColor: colors.tooltip.border,
        borderWidth: 1,
        padding: 12,
        cornerRadius: 8,
        callbacks: {
          label: (context) => {
            const value = context.parsed.y.toFixed(1);
            if (context.datasetIndex === 0) {
              const metric = weightData[context.dataIndex];
              const label = `Gewicht: ${value} kg`;
              return metric?.exclude_from_trends ? `${label} (ausgeschlossen)` : label;
            }
            return `7-Tage-Ø: ${value} kg`;
          }
        }
      }
    },
    scales: {
      y: {
        beginAtZero: false,
        title: {
          display: true,
          text: 'Gewicht (kg)',
          color: colors.textMuted,
        },
        ticks: {
          color: colors.textMuted,
        },
        grid: {
          color: colors.gridColor,
        }
      },
      x: {
        title: {
          display: true,
          text: 'Datum',
          color: colors.textMuted,
        },
        ticks: {
          color: colors.textMuted,
        },
        grid: {
          color: colors.gridColor,
        }
      }
    },
    interaction: {
      mode: 'nearest',
      axis: 'x',
      intersect: false
    }
  };

  if (weightData.length === 0) {
    return <p>Keine Gewichtsdaten verfügbar</p>;
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '300px' }}>
      <div style={{ flex: 1, minHeight: 0 }}>
        <Line data={data} options={options} />
      </div>
      {excludedCount > 0 && (
        <p style={{ textAlign: 'center', marginTop: '8px', fontSize: '14px', color: 'var(--text-secondary)', flexShrink: 0, opacity: 0.7 }}>
          {excludedCount} Tag{excludedCount !== 1 ? 'e' : ''} ausgeschlossen*
        </p>
      )}
    </div>
  );
}

export default WeightChart;
